import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { firestore } from "../firebase";

// 30 minutes in milliseconds
const EDIT_WINDOW_MS = 30 * 60 * 1000;

const AmendmentSection = ({ contractId, userEmail }) => {
  const [amendments, setAmendments] = useState([]);
  const [newAmendmentText, setNewAmendmentText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [loading, setLoading] = useState(true);

  // --- 1. Fetch all amendments for this contract ---
  useEffect(() => {
    setLoading(true);
    const amendmentsRef = collection(
      firestore,
      "ContractMaster",
      contractId,
      "amendments"
    );
    const q = query(amendmentsRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ams = [];
      snapshot.forEach((doc) => {
        ams.push({ id: doc.id, ...doc.data() });
      });
      setAmendments(ams);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [contractId]);

  // --- 2. Handle adding a new amendment ---
  const handleAddAmendment = async () => {
    if (!newAmendmentText.trim()) return;
    setLoading(true);
    try {
      const amendmentsRef = collection(
        firestore,
        "ContractMaster",
        contractId,
        "amendments"
      );
      await addDoc(amendmentsRef, {
        text: newAmendmentText,
        createdAt: serverTimestamp(),
        createdByEmail: userEmail,
      });
      setNewAmendmentText("");
    } catch (error) {
      console.error("Error adding amendment:", error);
    }
    setLoading(false);
  };

  // --- 3. Handle saving an edited amendment ---
  const handleSaveEdit = async (amendmentId) => {
    setLoading(true);
    try {
      const docRef = doc(
        firestore,
        "ContractMaster",
        contractId,
        "amendments",
        amendmentId
      );
      await updateDoc(docRef, {
        text: editingText,
        updatedAt: serverTimestamp(), // Note it was edited
      });
      setEditingId(null);
      setEditingText("");
    } catch (error) {
      console.error("Error updating amendment:", error);
    }
    setLoading(false);
  };

  // --- 4. Handle deleting an amendment (within the window) ---
  const handleDelete = async (amendmentId) => {
    if (!window.confirm("Are you sure you want to delete this amendment?")) {
      return;
    }
    setLoading(true);
    try {
      const docRef = doc(
        firestore,
        "ContractMaster",
        contractId,
        "amendments",
        amendmentId
      );
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting amendment:", error);
    }
    setLoading(false);
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 4 }, mt: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        Amendments
      </Typography>
      {loading && <CircularProgress sx={{ mb: 2 }} />}

      {/* --- List of all amendments --- */}
      <List>
        {amendments.map((am) => {
          // --- This is the 30-minute freeze logic ---
          const isEditable = am.createdAt?.toDate
            ? new Date() - am.createdAt.toDate() < EDIT_WINDOW_MS
            : false; // If no timestamp, not editable

          return (
            <React.Fragment key={am.id}>
              <ListItem
                sx={{
                  backgroundColor: isEditable ? "#fffbe6" : "#f9f9f9",
                  borderRadius: 1,
                  mb: 1,
                  alignItems: "flex-start",
                }}
                secondaryAction={
                  isEditable && !editingId ? ( // Only show if editable and not being edited
                    <Box>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => {
                          setEditingId(am.id);
                          setEditingText(am.text);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDelete(am.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ) : null
                }
              >
                {editingId === am.id ? (
                  // --- Show Edit Form ---
                  <Box sx={{ width: "100%" }}>
                    <TextField
                      fullWidth
                      multiline
                      variant="outlined"
                      value={editingText}
                      // ✅ --- THIS IS THE FIX ---
                      onChange={(e) => setEditingText(e.target.value)}
                    />
                    <Box sx={{ mt: 1, textAlign: "right" }}>
                      <Button onClick={() => setEditingId(null)}>Cancel</Button>
                      <Button onClick={() => handleSaveEdit(am.id)}>
                        Save
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  // --- Show Read-Only Text ---
                  <ListItemText
                    primary={am.text}
                    secondary={`By: ${am.createdByEmail} at ${am.createdAt
                      ?.toDate()
                      .toLocaleString()} ${
                      !isEditable ? "(Frozen)" : "(Editable for 30 min)"
                    }`}
                  />
                )}
              </ListItem>
              <Divider variant="inset" component="li" />
            </React.Fragment>
          );
        })}
      </List>

      {/* --- New Amendment Input --- */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Add New Amendment</Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          label="Type new amendment..."
          value={newAmendmentText}
          onChange={(e) => setNewAmendmentText(e.target.value)}
          sx={{ mt: 1, mb: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleAddAmendment}
          disabled={loading}
        >
          Add Amendment
        </Button>
      </Box>
    </Paper>
  );
};

export default AmendmentSection;
